import {useRouter} from 'next/router'
import {useCallback, useEffect, useState} from 'react'
import {useLeadIdContext} from '../context/LeadIdContext'

export type TourState = {
  activeTour: number
  showLeadForm: boolean
  tourStarted: boolean
}

export const useProductTour = (totalTours: number = 0) => {
  const {leadId} = useLeadIdContext()
  const router = useRouter()

  const getTourFromQuery = () => {
    if (!router.isReady) {
      return 0
    }

    const tourParam = router.query.tour

    if (typeof tourParam !== 'string') {
      return 0
    }

    const tourIndex = parseInt(tourParam, 10)
    return tourIndex >= 0 && tourIndex < totalTours ? tourIndex : 0
  }

  const [state, setState] = useState<TourState>({
    activeTour: 0,
    showLeadForm: true,
    tourStarted: false,
  })

  const setActiveTour = useCallback(
    (index: number) => {
      setState((prev) => ({...prev, activeTour: index}))

      // Update URL query parameter with shallow routing (no page reload)
      router.push(
        {
          pathname: router.pathname,
          query: {...router.query, tour: index.toString()},
        },
        undefined,
        {shallow: true},
      )
    },
    [router],
  )

  const startTour = useCallback(() => {
    setState((prev) => ({...prev, tourStarted: true}))
  }, [])

  const handleLeadSubmit = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showLeadForm: false,
      tourStarted: true,
    }))
  }, [])

  // Sync state with query parameter (initialization + browser navigation)
  useEffect(() => {
    if (router.isReady) {
      const tourIndex = getTourFromQuery()
      const utmSource = router.query.utm_source
      const shouldHideLeadForm = typeof utmSource === 'string' && utmSource === 'SALES'

      setState((prev) => ({
        ...prev,
        activeTour: tourIndex,
        showLeadForm: shouldHideLeadForm ? false : prev.showLeadForm,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getTourFromQuery uses router and totalTours which are stable
  }, [router.isReady, router.query.tour, router.query.utm_source])

  // React to leadId changes - hide form and start tour when leadId exists
  useEffect(() => {
    if (leadId) {
      setState((prev) => ({...prev, showLeadForm: false, tourStarted: true}))
    }
  }, [leadId])

  return {
    state,
    setActiveTour,
    startTour,
    handleLeadSubmit,
  }
}
